// 42 Common Core 과제 카드 어댑터.
// scripts/fetch42Projects.mjs 가 만든 generated.json(사실 데이터) 과 curated.js(요약·Skills) 를
// Resume.jsx 의 Experience 카드 객체로 바꾼다. 기간·duration 표기는 기존 카드의 규칙을 따른다.
import generated from './fortyTwoProjects.generated.json';
import { fortyTwoCurated } from './fortyTwoProjects.curated';

const parseDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d, t: Date.UTC(y, m - 1, d) };
};

export const formatKoreanDate = (iso) => {
  const p = parseDate(iso);
  return p ? `${p.y}년 ${p.m}월 ${p.d}일` : '';
};

// '2025년 12월 3일 ~ 4일' / '2025년 10월 20일 ~ 11월 14일' / '2025년 12월 15일 ~ 2026년 1월 5일'
export const formatPeriod = (startIso, endIso) => {
  const s = parseDate(startIso);
  const e = parseDate(endIso);
  if (!s) return '';
  const start = `${s.y}년 ${s.m}월 ${s.d}일`;
  if (!e) return `${start} ~ 현재`;
  if (e.y !== s.y) return `${start} ~ ${e.y}년 ${e.m}월 ${e.d}일`;
  if (e.m !== s.m) return `${start} ~ ${e.m}월 ${e.d}일`;
  if (e.d !== s.d) return `${start} ~ ${e.d}일`;
  return start;
};

// '약 2일' / '약 4주' / '약 2개월' (시작·끝 날짜를 모두 포함해 센다)
export const formatDuration = (startIso, endIso) => {
  const s = parseDate(startIso);
  const e = parseDate(endIso);
  if (!s) return '';
  if (!e) return '진행 중';
  const days = Math.max(1, Math.round((e.t - s.t) / 86400000) + 1);
  if (days < 14) return `약 ${days}일`;
  if (days < 60) return `약 ${Math.round(days / 7)}주`;
  return `약 ${Math.max(2, Math.round(days / 30))}개월`;
};

const LANGUAGE_SKILLS = {
  C: '#C언어',
  'C++': '#C++',
  Makefile: '#Makefile',
  Shell: '#셸스크립트',
  Python: '#Python',
  JavaScript: '#자바스크립트',
  TypeScript: '#타입스크립트',
  Dockerfile: '#Docker',
  HTML: '#HTML5',
  CSS: '#CSS3',
};
const languageToSkill = (lang) => LANGUAGE_SKILLS[lang] || `#${lang}`;

// Resume.jsx 의 experiences 항목과 같은 모양 + milestone / evaluation / exam / fortyTwoSlug
export const buildFortyTwoExperiences = () => {
  const projects = Array.isArray(generated?.projects) ? generated.projects : [];
  return [...projects]
    .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
    .map((p) => {
      const curated = fortyTwoCurated[p.slug] || {};
      const isExam = !!p.exam; // 시험: 동료평가·저장소 없음, 회차별 점수(examHistory)만
      const skills =
        Array.isArray(curated.skills) && curated.skills.length > 0
          ? curated.skills
          : (p.languages || []).map(languageToSkill);
      const evaluation = !isExam && p.evaluation
        ? {
            finalMark: p.finalMark ?? null,
            count: p.evaluation.count || 0,
            outstanding: p.evaluation.outstanding || 0,
            summaries: Array.isArray(curated.summaries) ? curated.summaries : [],
          }
        : null;
      const exam = isExam
        ? {
            finalMark: p.finalMark ?? null,
            attempts: p.attempts ?? (Array.isArray(p.examHistory) ? p.examHistory.length : null),
            passedOn: p.endDate || null,
            history: Array.isArray(p.examHistory) ? p.examHistory : [],
          }
        : null;
      return {
        period: formatPeriod(p.startDate, p.endDate),
        duration: formatDuration(p.startDate, p.endDate),
        title: p.name,
        // 소개: curated 의 한국어 번역(aboutKo)이 있으면 그것, 없으면 GitHub About 원문
        description: curated.aboutKo || p.about || (isExam ? '시험 소개 미기재' : 'GitHub About 미기재'),
        aboutOriginal: p.about || null,
        skills,
        color: 'cyan',
        isEducation: true,
        // 🔗 GitHub 저장소(시험·저장소 없는 과제는 없음). 카드 링크는 배열 (CLAUDE.md Phase 26)
        links: !isExam && p.github ? [{ url: p.github }] : undefined,
        milestone: p.milestone ?? null,
        evaluation,
        isExam,
        exam,
        fortyTwoSlug: p.slug,
      };
    });
};
